import React from 'react';
import { Printer, X } from 'lucide-react';

export const InvoicePrintView = ({ invoice, onClose }) => {
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = document.querySelector('.invoice-print-content');

    const contentDiv = document.createElement('div');
    contentDiv.innerHTML = printContent.innerHTML;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${invoice.invoice_number}</title>
          <style>
            body {
              font-family: system-ui, -apple-system, sans-serif;
              margin: 0;
              padding: 16px;
              line-height: 1.4;
            }
            .invoice-header {
              text-align: center;
              margin-bottom: 2rem;
              border-bottom: 3px solid #f97316;
              padding-bottom: 1rem;
            }
            .company-logo {
              height: 2.5rem;
              max-width: 12rem;
              margin: 0 auto 0.75rem;
              display: block;
            }
            .company-info {
              color: #4b5563;
              font-size: 0.875rem;
              margin-top: 0.5rem;
            }
            .company-info p {
              margin: 0.15rem 0;
            }
            .invoice-title {
              background-color: #f97316;
              color: white;
              padding: 0.5rem 1rem;
              border-radius: 0.375rem;
              font-size: 1.5rem;
              font-weight: 700;
              display: inline-block;
              margin-bottom: 1rem;
            }
            .grid { display: grid; }
            .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            .gap-6 { gap: 1.5rem; }
            .gap-4 { gap: 1rem; }
            .mb-6 { margin-bottom: 1.5rem; }
            .mb-4 { margin-bottom: 1rem; }
            .mb-3 { margin-bottom: 0.75rem; }
            .mb-2 { margin-bottom: 0.5rem; }
            .mt-6 { margin-top: 1.5rem; }
            .mt-4 { margin-top: 1rem; }
            .p-4 { padding: 1rem; }
            .p-3 { padding: 0.75rem; }
            .px-4 { padding-left: 1rem; padding-right: 1rem; }
            .py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
            .bg-gray-50 { background-color: #f9fafb; }
            .bg-orange-50 { background-color: #fff7ed; }
            .bg-green-50 { background-color: #f0fdf4; }
            .rounded-lg { border-radius: 0.5rem; }
            .border { border: 1px solid #d1d5db; }
            .border-l-4 { border-left: 4px solid; }
            .border-orange-400 { border-left-color: #f97316; }
            .border-t { border-top: 1px solid #d1d5db; }
            .border-t-2 { border-top: 2px solid #d1d5db; }
            .border-gray-300 { border-color: #d1d5db; }
            .border-gray-200 { border-color: #e5e7eb; }
            .font-bold { font-weight: 700; }
            .font-semibold { font-weight: 600; }
            .font-medium { font-weight: 500; }
            .text-3xl { font-size: 1.875rem; }
            .text-2xl { font-size: 1.5rem; }
            .text-xl { font-size: 1.25rem; }
            .text-lg { font-size: 1.125rem; }
            .text-sm { font-size: 0.875rem; }
            .text-xs { font-size: 0.75rem; }
            .text-gray-900 { color: #111827; }
            .text-gray-800 { color: #1f2937; }
            .text-gray-700 { color: #374151; }
            .text-gray-600 { color: #4b5563; }
            .text-gray-500 { color: #6b7280; }
            .text-orange-600 { color: #ea580c; }
            .text-green-700 { color: #15803d; }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .space-y-1 > * + * { margin-top: 0.25rem; }
            .space-y-2 > * + * { margin-top: 0.5rem; }
            .flex { display: flex; }
            .justify-between { justify-content: space-between; }
            .items-center { align-items: center; }
            .whitespace-pre-wrap { white-space: pre-wrap; }
            table { width: 100%; border-collapse: collapse; margin-top: 1rem; }
            th, td { padding: 0.75rem; text-align: left; border-bottom: 1px solid #e5e7eb; }
            th { background-color: #f9fafb; font-weight: 600; color: #374151; font-size: 0.875rem; text-transform: uppercase; }
            .text-right th, .text-right td { text-align: right; }
            tbody tr:last-child td { border-bottom: none; }
            .total-row { border-top: 2px solid #d1d5db; background-color: #f0fdf4; }
            .total-row td { padding: 1rem 0.75rem; font-size: 1.125rem; font-weight: 700; }
            .payment-terms {
              background-color: #fff7ed;
              border: 2px solid #f97316;
              padding: 1rem;
              margin-top: 2rem;
              border-radius: 0.5rem;
            }
            .footer {
              margin-top: 3rem;
              padding-top: 1.5rem;
              border-top: 2px solid #d1d5db;
              text-align: center;
              color: #6b7280;
              font-size: 0.75rem;
            }
            @page { margin: 0.5in; }
            @media print {
              body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${contentDiv.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateSubtotal = () => {
    if (!invoice.items || invoice.items.length === 0) {
      // Fallback to total_amount if no items (shouldn't happen after editing)
      return invoice.total_amount / 1.13; // Reverse calculate subtotal from total
    }
    return invoice.items.reduce((sum, item) =>
      sum + parseFloat(item.amount || 0), 0
    );
  };

  const subtotal = calculateSubtotal();
  const taxRate = 0.13; // 13% HST for Ontario
  const taxAmount = subtotal * taxRate;
  const total = subtotal + taxAmount;

  return (
    <div className="fixed inset-0 bg-gray-100 z-50 overflow-auto">
      <div className="min-h-screen">
        {/* Header Controls */}
        <div className="sticky top-0 z-10 bg-white shadow-md border-b no-print">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Invoice Preview</h2>
              <div className="flex space-x-2">
                <button
                  onClick={handlePrint}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <Printer size={16} />
                  <span>Print</span>
                </button>
                <button
                  onClick={onClose}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <X size={16} />
                  <span>Close</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Print Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white shadow-lg rounded-lg invoice-print-content p-6 sm:p-8 lg:p-12">
          {/* Header */}
          <div className="invoice-header">
            <img
              src={`${window.location.origin}/jft-logo.png`}
              alt="JFT Contracting"
              className="company-logo"
              style={{ height: '2.5rem', maxWidth: '12rem', margin: '0 auto 0.75rem', display: 'block' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="company-info">
              <p className="font-semibold text-lg">JFT Contracting Ltd.</p>
              <p>Professional Contracting Services</p>
              <p>Thunder Bay, Ontario • (807) 555-0123</p>
              <p>info@jftcontracting.com • www.jftcontracting.com</p>
            </div>
          </div>

          <div className="text-center mb-6">
            <div className="invoice-title">INVOICE</div>
          </div>

          {/* Invoice Details and Customer Info */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Bill To */}
            <div className="border-l-4 border-orange-400 pl-4">
              <h3 className="text-sm font-semibold text-gray-500 mb-2">BILL TO</h3>
              <div className="space-y-1">
                <p className="text-lg font-bold text-gray-900">
                  {invoice.customer?.first_name} {invoice.customer?.last_name}
                </p>
                {invoice.customer?.company && <p className="text-gray-800">{invoice.customer.company}</p>}
                {invoice.customer?.address && <p className="text-gray-700">{invoice.customer.address}</p>}
                {invoice.customer?.city && (
                  <p className="text-gray-700">
                    {invoice.customer.city}, {invoice.customer.province} {invoice.customer.postal_code}
                  </p>
                )}
                {invoice.customer?.email && <p className="text-gray-700">{invoice.customer.email}</p>}
                {invoice.customer?.phone && <p className="text-gray-700">{invoice.customer.phone}</p>}
              </div>
            </div>

            {/* Invoice Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Invoice Number:</span>
                  <span className="text-gray-900 font-bold">{invoice.invoice_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Invoice Date:</span>
                  <span className="text-gray-900">{formatDate(invoice.invoice_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Due Date:</span>
                  <span className="text-gray-900 font-semibold">{formatDate(invoice.due_date)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-600 font-medium">Job Number:</span>
                  <span className="text-gray-900">{invoice.job?.job_number}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Job Location */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">JOB LOCATION</h3>
            <p className="text-gray-800">{invoice.job?.job_address}</p>
            <p className="text-gray-800">
              {invoice.job?.job_city}, {invoice.job?.job_province} {invoice.job?.job_postal_code}
            </p>
            {invoice.job?.completion_date && (
              <p className="text-sm text-gray-600 mt-2">
                Completed: {formatDate(invoice.job.completion_date)}
              </p>
            )}
          </div>

          {/* Line Items */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Services Provided</h3>
            {invoice.items && invoice.items.length > 0 ? (
              <table className="border border-gray-200">
                <thead>
                  <tr>
                    <th className="text-left">Description</th>
                    <th className="text-center" style={{ width: '100px' }}>Quantity</th>
                    <th className="text-right" style={{ width: '120px' }}>Unit Price</th>
                    <th className="text-right" style={{ width: '120px' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item, index) => (
                    <tr key={index}>
                      <td className="text-gray-800">{item.description}</td>
                      <td className="text-center text-gray-700">{item.quantity}</td>
                      <td className="text-right text-gray-700">{formatCurrency(item.unit_price)}</td>
                      <td className="text-right font-medium text-gray-900">
                        {formatCurrency(item.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="border border-gray-200 p-4 rounded-lg bg-gray-50">
                <p className="text-gray-700">Professional contracting services as per job specification</p>
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="mb-6">
            <table className="w-full">
              <tbody>
                <tr>
                  <td colSpan="2"></td>
                  <td className="text-right font-medium text-gray-700 border-t-2 border-gray-300 pt-3" style={{ width: '120px' }}>Subtotal:</td>
                  <td className="text-right text-gray-700 border-t-2 border-gray-300 pt-3" style={{ width: '120px' }}>{formatCurrency(subtotal)}</td>
                </tr>
                <tr>
                  <td colSpan="2"></td>
                  <td className="text-right font-medium text-gray-700 pt-2" style={{ width: '120px' }}>HST (13%):</td>
                  <td className="text-right text-gray-700 pt-2" style={{ width: '120px' }}>{formatCurrency(taxAmount)}</td>
                </tr>
                <tr>
                  <td colSpan="2"></td>
                  <td className="text-right font-bold text-gray-900 border-t-2 border-gray-400 pt-3 text-lg" style={{ width: '120px' }}>Total Amount:</td>
                  <td className="text-right font-bold text-orange-600 border-t-2 border-gray-400 pt-3 text-xl" style={{ width: '120px' }}>{formatCurrency(total)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">NOTES</h3>
              <p className="text-gray-700 text-sm whitespace-pre-wrap">{invoice.notes}</p>
            </div>
          )}

          {/* Payment Terms */}
          <div className="payment-terms">
            <h3 className="font-bold text-gray-900 mb-2 text-center">PAYMENT TERMS</h3>
            <div className="text-sm text-gray-700 space-y-1">
              <p className="text-center">Payment is due by <strong>{formatDate(invoice.due_date)}</strong></p>
              <p className="text-center">Please make checks payable to <strong>JFT Contracting Ltd.</strong></p>
              <p className="text-center">E-Transfer: payments@jftcontracting.com</p>
            </div>
          </div>

          {/* Footer */}
          <div className="footer">
            <p className="font-semibold mb-1">Thank you for your business!</p>
            <p>For questions about this invoice, please contact us at (807) 555-0123 or info@jftcontracting.com</p>
            <p className="mt-2">JFT Contracting Ltd. • Business Number: 123456789 RT0001</p>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};
